import type {
  CreateRoleRequest,
  EffectivePermissionsResponse,
  EffectiveSettings,
  InviteUserRequest,
  InviteUserResponse,
  OrgUser,
  RoleResponse,
  SettingKey,
  SettingRow,
  SettingValue,
  UpdateRoleRequest,
} from '@palaccount/contracts';
import { SETTINGS_REGISTRY } from '@palaccount/contracts';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';

import { api } from '@/api';
import { throwApiError } from '@/lib/server-messages';

/**
 * The admin wave's data layer: settings (ADM-01), users (ADM-05), roles
 * (ADM-06) — thin TanStack Query wrappers over the typed contract client,
 * same policy as every feature.
 */

export const SETTINGS_KEY = ['settings'] as const;
export const EFFECTIVE_KEY = ['settings', 'effective'] as const;
export const USERS_KEY = ['org-users'] as const;
export const ROLES_KEY = ['security-roles'] as const;

// ── Settings ────────────────────────────────────────────────────────────────

export function useSettings() {
  return useQuery({
    queryKey: SETTINGS_KEY,
    queryFn: async (): Promise<SettingRow[]> => {
      const response = await api.orgSettings.list();
      if (response.status !== 200) throwApiError(response.status, response.body);
      return response.body.settings;
    },
  });
}

/**
 * The caller's effective org/role/personal values — what every OTHER screen
 * reads for its behavioural defaults (PO approval banner, VAT default, count
 * tolerance). Cached briefly; the server enforces regardless, so staleness
 * here costs a banner, never a rule.
 */
export function useEffectiveSettings() {
  return useQuery({
    queryKey: EFFECTIVE_KEY,
    staleTime: 60_000,
    queryFn: async (): Promise<EffectiveSettings['settings']> => {
      const response = await api.orgSettings.effective();
      if (response.status !== 200) throwApiError(response.status, response.body);
      return response.body.settings;
    },
  });
}

/**
 * One effective setting with the registry default as the loading fallback —
 * optimistic on purpose: the server re-checks everything at the write.
 */
export function useOrgSetting<T extends SettingValue>(key: SettingKey): T {
  const { data } = useEffectiveSettings();
  return ((data?.[key] as T | undefined) ?? (SETTINGS_REGISTRY[key].default as T)) as T;
}

export function useUpdateSetting() {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: async (input: { key: string; value: SettingValue }): Promise<SettingRow> => {
      const response = await api.orgSettings.update({
        params: { key: input.key },
        body: { value: input.value },
      });
      if (response.status !== 200) throwApiError(response.status, response.body);
      return response.body;
    },
    onSuccess: () => {
      void queryClient.invalidateQueries({ queryKey: SETTINGS_KEY });
      // The Print button reads `print.*` off the effective map (INV-H6); a
      // saved option must reach the next print, not the one after the cache
      // expires.
      void queryClient.invalidateQueries({ queryKey: EFFECTIVE_KEY });
    },
  });
}

export function useLockSetting() {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: async (input: { key: string; locked: boolean }): Promise<SettingRow> => {
      const response = await api.orgSettings.lock({
        params: { key: input.key },
        body: { locked: input.locked },
      });
      if (response.status !== 200) throwApiError(response.status, response.body);
      return response.body;
    },
    onSuccess: () => {
      void queryClient.invalidateQueries({ queryKey: SETTINGS_KEY });
    },
  });
}

export function useOverrideSetting() {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: async (input: {
      key: string;
      value: SettingValue | null;
    }): Promise<SettingRow> => {
      const response =
        input.value === null
          ? await api.orgSettings.clearOverride({ params: { key: input.key } })
          : await api.orgSettings.override({
              params: { key: input.key },
              body: { value: input.value },
            });
      if (response.status !== 200) throwApiError(response.status, response.body);
      return response.body;
    },
    onSuccess: () => {
      void queryClient.invalidateQueries({ queryKey: SETTINGS_KEY });
    },
  });
}

// ── Users ───────────────────────────────────────────────────────────────────

export function useOrgUsers() {
  return useQuery({
    queryKey: USERS_KEY,
    queryFn: async (): Promise<OrgUser[]> => {
      const response = await api.users.list();
      if (response.status !== 200) throwApiError(response.status, response.body);
      return response.body.users;
    },
  });
}

export function useInviteUser() {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: async (body: InviteUserRequest): Promise<InviteUserResponse> => {
      const response = await api.users.invite({ body });
      if (response.status !== 201) throwApiError(response.status, response.body);
      return response.body;
    },
    onSuccess: () => {
      void queryClient.invalidateQueries({ queryKey: USERS_KEY });
    },
  });
}

export function useUserAction() {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: async (input: {
      userId: string;
      action: 'suspend' | 'reactivate' | 'revoke-invite';
    }): Promise<void> => {
      const params = { params: { userId: input.userId }, body: {} };
      const response =
        input.action === 'suspend'
          ? await api.users.suspend(params)
          : input.action === 'reactivate'
            ? await api.users.reactivate(params)
            : await api.users.revokeInvite(params);
      if (response.status !== 200) throwApiError(response.status, response.body);
    },
    onSuccess: () => {
      void queryClient.invalidateQueries({ queryKey: USERS_KEY });
    },
  });
}

export function useResendInvite() {
  return useMutation({
    mutationFn: async (userId: string): Promise<InviteUserResponse> => {
      const response = await api.users.resendInvite({ params: { userId }, body: {} });
      if (response.status !== 201) throwApiError(response.status, response.body);
      return response.body;
    },
  });
}

export function useAssignRole() {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: async (input: { userId: string; roleId: string | null }) => {
      const response = await api.roles.assign({
        params: { userId: input.userId },
        body: { roleId: input.roleId },
      });
      if (response.status !== 200) throwApiError(response.status, response.body);
      return response.body;
    },
    onSuccess: () => {
      void queryClient.invalidateQueries({ queryKey: USERS_KEY });
      void queryClient.invalidateQueries({ queryKey: ROLES_KEY });
    },
  });
}

/**
 * G-08 "View as" — the permission list another person would get from /api/me.
 * A GET, held as a mutation because it is fired by a row action and its answer
 * is handed straight to the view-as store rather than cached: the preview must
 * start from what the server says NOW, not from a list read minutes ago.
 */
export function useEffectivePermissions() {
  return useMutation({
    mutationFn: async (userId: string): Promise<EffectivePermissionsResponse> => {
      const response = await api.users.effectivePermissions({ params: { userId } });
      if (response.status !== 200) throwApiError(response.status, response.body);
      return response.body;
    },
  });
}

export function useTransferOwnership() {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: async (input: { userId: string; confirmName: string }) => {
      const response = await api.users.transferOwnership({
        params: { userId: input.userId },
        body: { confirmName: input.confirmName },
      });
      if (response.status !== 200) throwApiError(response.status, response.body);
      return response.body;
    },
    onSuccess: () => {
      void queryClient.invalidateQueries({ queryKey: USERS_KEY });
      void queryClient.invalidateQueries({ queryKey: ['me'] });
    },
  });
}

// ── Roles ───────────────────────────────────────────────────────────────────

/**
 * The company's roles with their grids (ADM-06, `admin.view` — BR-PERM-05).
 *
 * `enabled` is optional and defaults to on, so the two screens that have
 * always called this are unchanged. ADM-01's "Viewing as" control passes it:
 * that screen opens for anybody holding `admin.view`, but the control itself
 * is the Owner's and the Administrator's alone (OQ-15-11), so an Accountant
 * reading ADM-01 must not be made to fetch a grid nothing will draw.
 */
export function useSecurityRoles(options: { enabled?: boolean } = {}) {
  return useQuery({
    queryKey: ROLES_KEY,
    enabled: options.enabled ?? true,
    queryFn: async (): Promise<RoleResponse[]> => {
      const response = await api.roles.list();
      if (response.status !== 200) throwApiError(response.status, response.body);
      return response.body.roles;
    },
  });
}

export function useRoleMutations() {
  const queryClient = useQueryClient();
  const invalidate = () => {
    void queryClient.invalidateQueries({ queryKey: ROLES_KEY });
  };
  const create = useMutation({
    mutationFn: async (body: CreateRoleRequest): Promise<RoleResponse> => {
      const response = await api.roles.create({ body });
      if (response.status !== 201) throwApiError(response.status, response.body);
      return response.body;
    },
    onSuccess: invalidate,
  });
  const update = useMutation({
    mutationFn: async (input: { roleId: string; body: UpdateRoleRequest }): Promise<RoleResponse> => {
      const response = await api.roles.update({ params: { roleId: input.roleId }, body: input.body });
      if (response.status !== 200) throwApiError(response.status, response.body);
      return response.body;
    },
    onSuccess: invalidate,
  });
  const duplicate = useMutation({
    mutationFn: async (roleId: string): Promise<RoleResponse> => {
      const response = await api.roles.duplicate({ params: { roleId }, body: {} });
      if (response.status !== 201) throwApiError(response.status, response.body);
      return response.body;
    },
    onSuccess: invalidate,
  });
  const setActive = useMutation({
    mutationFn: async (input: { roleId: string; active: boolean }): Promise<RoleResponse> => {
      const params = { params: { roleId: input.roleId }, body: {} };
      const response = input.active
        ? await api.roles.reactivate(params)
        : await api.roles.deactivate(params);
      if (response.status !== 200) throwApiError(response.status, response.body);
      return response.body;
    },
    onSuccess: invalidate,
  });
  return { create, update, duplicate, setActive };
}
